import { toast } from "@/hooks/use-toast";

export interface ErrorContext {
  operation: string;
  component?: string;
  userId?: string;
  sessionId?: string;
  timestamp: number;
  userAgent: string;
  url: string;
}

export interface ErrorRecovery {
  canRetry: boolean;
  retryAction?: () => void;
  alternativeAction?: () => void;
  helpUrl?: string;
  contactSupport?: boolean;
}

export class AppError extends Error {
  constructor(
    message: string,
    public code?: string,
    public statusCode?: number,
    public context?: ErrorContext,
    public recovery?: ErrorRecovery
  ) {
    super(message);
    this.name = 'AppError';
  }
}

export class SessionError extends AppError {
  constructor(message: string, code?: string, context?: ErrorContext, recovery?: ErrorRecovery) {
    super(message, code, 400, context, recovery);
    this.name = 'SessionError';
  }
}

export class AuthError extends AppError {
  constructor(message: string, code?: string, context?: ErrorContext, recovery?: ErrorRecovery) {
    super(message, code, 401, context, recovery);
    this.name = 'AuthError';
  }
}

export class NetworkError extends AppError {
  constructor(message: string, code?: string, context?: ErrorContext, recovery?: ErrorRecovery) {
    super(message, code, 503, context, recovery);
    this.name = 'NetworkError';
  }
}

export class ValidationError extends AppError {
  constructor(message: string, field?: string, context?: ErrorContext) {
    super(message, 'VALIDATION_ERROR', 400, context, {
      canRetry: true,
      retryAction: () => {
        // Focus on the problematic field if available
        if (field) {
          const element = document.querySelector(`[name="${field}"]`) as HTMLElement;
          element?.focus();
        }
      }
    });
    this.name = 'ValidationError';
  }
}

export class ProviderMismatchError extends AppError {
  constructor(
    userProvider: string, 
    requestedProvider: string, 
    context?: ErrorContext
  ) {
    super(
      `You can only connect calendars matching your login provider. You signed in with ${userProvider} but tried to connect ${requestedProvider}.`,
      'PROVIDER_MISMATCH',
      400,
      context,
      {
        canRetry: false,
        alternativeAction: () => {
          toast({
            title: "Provider Mismatch",
            description: `Please sign out and sign in with ${requestedProvider} to use ${requestedProvider} Calendar integration.`,
            variant: "destructive",
          });
        },
        helpUrl: "https://support.deepfocus.com/calendar-integration"
      }
    );
    this.name = 'ProviderMismatchError';
  }
}

export class PlanRestrictionError extends AppError {
  constructor(feature: string, context?: ErrorContext) {
    super(
      `${feature} requires a Pro plan. Please upgrade to continue.`,
      'PLAN_RESTRICTION',
      403,
      context,
      {
        canRetry: false,
        alternativeAction: () => {
          toast({
            title: "Upgrade Required",
            description: `Please upgrade to Pro to access ${feature}.`,
            variant: "destructive",
          });
        },
        helpUrl: "https://deepfocus.com/pricing"
      }
    );
    this.name = 'PlanRestrictionError';
  }
}

export class OAuthProviderError extends AppError {
  constructor(provider: string, reason: string, context?: ErrorContext) {
    super(
      `OAuth provider error for ${provider}: ${reason}`,
      'OAUTH_PROVIDER_ERROR',
      400,
      context,
      {
        canRetry: true,
        retryAction: () => {
          // Redirect to re-authentication
          window.location.href = '/auth';
        },
        alternativeAction: () => {
          toast({
            title: "Authentication Error",
            description: `Please sign in again with ${provider} to continue.`,
            variant: "destructive",
          });
        },
        helpUrl: "https://support.deepfocus.com/authentication"
      }
    );
    this.name = 'OAuthProviderError';
  }
}

export class ChunkLoadError extends AppError {
  constructor(context?: ErrorContext) {
    super(
      'A new version of DeepFocus is available. Please refresh the page to continue.',
      'CHUNK_LOAD_ERROR',
      503,
      context,
      {
        canRetry: true,
        retryAction: () => {
          // Force hard reload to bypass cache
          window.location.reload();
        },
        alternativeAction: () => {
          toast({
            title: "Update Available",
            description: "A new version is available. Click 'Retry' to refresh.",
            variant: "default",
          });
        },
        helpUrl: "https://support.deepfocus.com/version-update"
      }
    );
    this.name = 'ChunkLoadError';
  }
}

/**
 * Checks if an error is a chunk loading error
 */
export function isChunkLoadError(error: unknown): boolean {
  if (error instanceof ChunkLoadError) return true;
  if (!(error instanceof Error)) return false;
  
  const message = error.message.toLowerCase();
  const name = error.name.toLowerCase();
  
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    message.includes('dynamically imported module') ||
    message.includes('importing a module script failed') ||
    message.includes('failed to load module script') ||
    name.includes('chunkloaderror') ||
    message.includes('unable to preload css') ||
    message.includes('error loading dynamically imported module')
  );
}

export function categorizeError(error: unknown): {
  type: 'network' | 'auth' | 'session' | 'validation' | 'provider' | 'plan' | 'chunk' | 'unknown';
  severity: 'low' | 'medium' | 'high' | 'critical';
  userFriendly: boolean;
} {
  // Check for chunk loading errors first
  if (isChunkLoadError(error)) {
    return { type: 'chunk', severity: 'medium', userFriendly: true };
  }
  if (error instanceof NetworkError) {
    return { type: 'network', severity: 'medium', userFriendly: true };
  }
  if (error instanceof AuthError) {
    return { type: 'auth', severity: 'high', userFriendly: true };
  }
  if (error instanceof SessionError) {
    return { type: 'session', severity: 'medium', userFriendly: true };
  }
  if (error instanceof ValidationError) {
    return { type: 'validation', severity: 'low', userFriendly: true };
  }
  if (error instanceof ProviderMismatchError) {
    return { type: 'provider', severity: 'medium', userFriendly: true };
  }
  if (error instanceof PlanRestrictionError) {
    return { type: 'plan', severity: 'medium', userFriendly: true };
  }
  if (error instanceof OAuthProviderError) {
    return { type: 'provider', severity: 'high', userFriendly: true };
  }
  if (error instanceof AppError) {
    return { type: 'unknown', severity: 'medium', userFriendly: true };
  }
  
  // Check for common error patterns
  const errorMessage = error instanceof Error ? error.message : String(error);
  
  if (errorMessage.includes('network') || errorMessage.includes('fetch')) {
    return { type: 'network', severity: 'medium', userFriendly: true };
  }
  if (errorMessage.includes('unauthorized') || errorMessage.includes('401')) {
    return { type: 'auth', severity: 'high', userFriendly: true };
  }
  if (errorMessage.includes('validation') || errorMessage.includes('invalid')) {
    return { type: 'validation', severity: 'low', userFriendly: true };
  }
  
  return { type: 'unknown', severity: 'critical', userFriendly: false };
}

export function getErrorRecoveryActions(error: unknown, context?: ErrorContext): ErrorRecovery {
  const categorized = categorizeError(error);
  
  switch (categorized.type) {
    case 'network':
      return {
        canRetry: true,
        retryAction: () => window.location.reload(),
        alternativeAction: () => {
          toast({
            title: "Connection Issues",
            description: "Please check your internet connection and try again.",
            variant: "destructive",
          });
        },
        helpUrl: "https://support.deepfocus.com/network-issues"
      };
    
    case 'auth':
      return {
        canRetry: true,
        retryAction: () => {
          localStorage.removeItem('deepFocus_session');
          window.location.href = '/auth';
        },
        alternativeAction: () => {
          toast({
            title: "Authentication Required",
            description: "Please sign in again to continue.",
            variant: "destructive",
          });
        },
        contactSupport: true
      };
    
    case 'session':
      return {
        canRetry: true,
        retryAction: () => window.location.href = '/dashboard',
        alternativeAction: () => {
          toast({
            title: "Session Issue",
            description: "Please return to dashboard and try again.",
            variant: "destructive",
          });
        }
      };
    
    case 'validation':
      return {
        canRetry: true,
        retryAction: () => {
          // Focus on form fields or retry the operation
          const form = document.querySelector('form');
          if (form) {
            const firstInput = form.querySelector('input, textarea, select') as HTMLElement;
            firstInput?.focus();
          }
        }
      };
    
    case 'provider':
      return {
        canRetry: true,
        retryAction: () => {
          // Redirect to re-authentication
          window.location.href = '/auth';
        },
        alternativeAction: () => {
          toast({
            title: "Provider Error",
            description: "Please sign out and sign in again with the correct provider.",
            variant: "destructive",
          });
        },
        helpUrl: "https://support.deepfocus.com/calendar-integration"
      };
    
    case 'plan':
      return {
        canRetry: false,
        alternativeAction: () => {
          // Redirect to upgrade page
          window.location.href = '/pricing';
        },
        helpUrl: "https://deepfocus.com/pricing"
      };
    
    case 'chunk':
      return {
        canRetry: true,
        retryAction: () => {
          // Force hard reload to bypass cache and get fresh assets
          window.location.reload();
        },
        alternativeAction: () => {
          toast({
            title: "Update Available",
            description: "A new version of DeepFocus is available. Please refresh the page.",
            variant: "default",
          });
        },
        helpUrl: "https://support.deepfocus.com/version-update"
      };
    
    default:
      return {
        canRetry: false,
        contactSupport: true,
        helpUrl: "https://support.deepfocus.com/contact"
      };
  }
}

export function createErrorContext(operation: string, component?: string): ErrorContext {
  return {
    operation,
    component,
    userId: localStorage.getItem('deepFocus_userId') || undefined,
    sessionId: localStorage.getItem('deepFocus_sessionId') || undefined,
    timestamp: Date.now(),
    userAgent: navigator.userAgent,
    url: window.location.href
  };
}

export function handleError(error: unknown, context?: string) {
  const errorContext = createErrorContext(context || 'application');
  const recovery = getErrorRecoveryActions(error, errorContext);
  const categorized = categorizeError(error);
  
  console.error(`Error in ${context || 'application'}:`, {
    error,
    context: errorContext,
    categorization: categorized,
    recovery
  });
  
  if (error instanceof AppError) {
    // Don't show toast for CORS errors - they're expected when Edge Function CORS isn't configured
    // The calling code will fall back to direct database update
    if (error.code === 'CORS_ERROR') {
      console.log('ℹ️ CORS error detected, skipping error toast (fallback to direct DB update)');
      return;
    }
    
    const actions = [];
    if (recovery.canRetry && recovery.retryAction) {
      actions.push({
        label: "Retry",
        onClick: recovery.retryAction
      });
    }
    if (recovery.alternativeAction) {
      actions.push({
        label: "Try Alternative",
        onClick: recovery.alternativeAction
      });
    }
    if (recovery.helpUrl) {
      actions.push({
        label: "Get Help",
        onClick: () => window.open(recovery.helpUrl, '_blank')
      });
    }
    
    toast({
      title: categorized.severity === 'critical' ? "Critical Error" : "Error",
      description: error.message,
      variant: "destructive",
      action: actions.length > 0 ? actions[0] : undefined,
    });
    return;
  }
  
  if (error instanceof Error) {
    const message = categorized.userFriendly 
      ? "An error occurred. Please try again."
      : error.message;
    
    toast({
      title: categorized.severity === 'critical' ? "Critical Error" : "Unexpected Error",
      description: message,
      variant: "destructive",
      action: recovery.canRetry ? {
        label: "Retry",
        onClick: recovery.retryAction || (() => window.location.reload())
      } : undefined,
    });
    return;
  }
  
  toast({
    title: "Something went wrong",
    description: "An unexpected error occurred. Please try again.",
    variant: "destructive",
    action: recovery.canRetry ? {
      label: "Retry",
      onClick: recovery.retryAction || (() => window.location.reload())
    } : undefined,
  });
}

export function withErrorHandling<T extends unknown[], R>(
  fn: (...args: T) => Promise<R>,
  context?: string
) {
  return async (...args: T): Promise<R | undefined> => {
    try {
      return await fn(...args);
    } catch (error) {
      handleError(error, context);
      return undefined;
    }
  };
}

export function withRetry<T extends unknown[], R>(
  fn: (...args: T) => Promise<R>,
  maxRetries: number = 3,
  delay: number = 1000,
  context?: string
) {
  return async (...args: T): Promise<R> => {
    let lastError: unknown;
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        return await fn(...args);
      } catch (error) {
        lastError = error;
        
        // Check if it's a CORS error - don't retry, just throw immediately
        const isCorsError = error instanceof AppError && error.code === 'CORS_ERROR';
        
        if (isCorsError || attempt === maxRetries) {
          // For CORS errors, don't call handleError (it will be handled by caller)
          // For last attempt, call handleError only if not CORS
          if (!isCorsError) {
            handleError(error, context);
          }
          // Re-throw the error so caller can handle it
          throw error;
        }
        
        // Wait before retrying
        await new Promise(resolve => setTimeout(resolve, delay * attempt));
      }
    }
    
    // This should never be reached, but TypeScript needs it
    throw lastError;
  };
}